/**
 * File List — Issue #557
 * Searchable sortable table of all files in the import graph.
 */

import { useState, useMemo } from 'react';
import type { ImportGraphData, ImportGraphNode } from './importGraphFlowConverter';
import { ROLE_COLORS } from './importGraphFlowConverter';

interface FileListProps {
  data: ImportGraphData;
  selectedFileId?: string;
  onSelectFile?: (fileId: string) => void;
  onViewInExplorer?: (fileId: string) => void;
  onViewSubgraph?: (fileId: string) => void;
  // Lifted filter state from page (issue #566)
  searchQuery?: string;
  roleFilters?: Set<string>;
  showCircularOnly?: boolean;
}

type SortField = 'name' | 'role' | 'importers' | 'imports' | 'exports';

export function FileList({ data, selectedFileId, onSelectFile, onViewInExplorer, onViewSubgraph, searchQuery = '', roleFilters = new Set(), showCircularOnly = false }: FileListProps) {
  const [sortField, setSortField] = useState<SortField>('importers');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filtered = useMemo(() => {
    let nodes: ImportGraphNode[] = [...data.graph.nodes];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      nodes = nodes.filter(n => n.name.toLowerCase().includes(q) || n.filePath.toLowerCase().includes(q));
    }
    if (roleFilters.size > 0) {
      nodes = nodes.filter(n => roleFilters.has(n.role));
    }
    if (showCircularOnly) {
      nodes = nodes.filter(n => n.hasCircularDep);
    }

    nodes.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'role': cmp = a.role.localeCompare(b.role); break;
        case 'importers': cmp = a.importedBy.length - b.importedBy.length; break;
        case 'imports': cmp = a.imports.length - b.imports.length; break;
        case 'exports': cmp = a.exportCount - b.exportCount; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return nodes;
  }, [data.graph.nodes, searchQuery, roleFilters, sortField, sortDir, showCircularOnly]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => (
    <span className="ml-1 text-gray-400">
      {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Count bar — filters are in page header (issue #566) */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
        <span className="text-sm text-gray-400">{filtered.length} of {data.graph.nodes.length} files</span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th
                className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => handleSort('name')}
              >
                File <SortIcon field="name" />
              </th>
              <th
                className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 w-28"
                onClick={() => handleSort('role')}
              >
                Role <SortIcon field="role" />
              </th>
              <th
                className="px-4 py-2 text-right font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 w-20"
                onClick={() => handleSort('importers')}
              >
                Importers <SortIcon field="importers" />
              </th>
              <th
                className="px-4 py-2 text-right font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 w-20"
                onClick={() => handleSort('imports')}
              >
                Imports <SortIcon field="imports" />
              </th>
              <th
                className="px-4 py-2 text-right font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 w-20"
                onClick={() => handleSort('exports')}
              >
                Exports <SortIcon field="exports" />
              </th>
              <th className="px-4 py-2 w-36" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map(n => (
              <tr
                key={n.id}
                className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  selectedFileId === n.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
                onClick={() => onSelectFile?.(n.id)}
              >
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ROLE_COLORS[n.role] }} />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{n.name}</div>
                      <div className="text-xs text-gray-400 truncate max-w-xs" title={n.filePath}>{n.filePath}</div>
                    </div>
                    {n.hasCircularDep && <span className="text-red-500 text-xs">⚠</span>}
                    {n.isBarrel && <span className="text-cyan-500 text-xs">B</span>}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: ROLE_COLORS[n.role] }}>
                    {n.role}
                  </span>
                </td>
                <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {n.importedBy.length}
                </td>
                <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {n.imports.length}
                </td>
                <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {n.exportCount}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    {onViewInExplorer && (
                      <button
                        onClick={e => { e.stopPropagation(); onViewInExplorer(n.id); }}
                        className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                      >
                        Explore
                      </button>
                    )}
                    {onViewSubgraph && (
                      <button
                        onClick={e => { e.stopPropagation(); onViewSubgraph(n.id); }}
                        className="text-xs text-purple-600 hover:underline dark:text-purple-400"
                        title="Open focused subgraph for this file"
                      >
                        Subgraph
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
            No files match the current filters
          </div>
        )}
      </div>
    </div>
  );
}
